import React, { FC, useContext, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import { ConfigContext, TestCardContext } from "@app/provider";
import PersonaToolingService from "@app/service/personaTooling";

interface PersonaSummary {
  id: string;
  testTypes: string[];
  orderCount: number;
  comment?: string | null;
}

interface ListPersonasData {
  listPersonas: PersonaSummary[];
}

interface ToolingResult {
  success: boolean;
  orderCount: number;
  message?: string | null;
}

type Status = { kind: "ok" | "error"; text: string } | null;

/**
 * Internal-only (local/staging) persona tooling. Renders nothing unless the
 * backend advertises `personaTooling.enabled` via /config — which is hard-blocked
 * in production. Lets an internal user load a persona (picker) or paste a raw SOS
 * response (inject) without the +test email hack.
 */
const PersonaToolingPanel: FC = () => {
  const { config } = useContext(ConfigContext);
  const enabled = Boolean(config?.personaTooling?.enabled);
  const { getTestsDataForHomePage } = useContext(TestCardContext);

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [payload, setPayload] = useState<string>("");
  const [status, setStatus] = useState<Status>(null);

  const { data, loading } = useQuery<ListPersonasData>(
    PersonaToolingService.listPersonas(),
    { skip: !enabled || !open }
  );

  const onDone = (result?: ToolingResult): void => {
    if (!result?.success) return;
    setStatus({ kind: "ok", text: `${result.message ?? "Done."} Refreshing…` });
    // Re-run the homepage query through the TestCard provider so its state (and
    // every consuming view) updates in place, no full page reload.
    getTestsDataForHomePage()
      .then(() => setStatus({ kind: "ok", text: result.message ?? "Done." }))
      .catch(onError);
  };

  const onError = (e: unknown): void => {
    setStatus({
      kind: "error",
      text: e instanceof Error ? e.message : "Request failed.",
    });
  };

  const [activatePersona, { loading: activating }] = useMutation(
    PersonaToolingService.activatePersona(),
    {
      onCompleted: (d) => onDone(d?.activatePersona),
      onError,
    }
  );

  const [injectPersona, { loading: injecting }] = useMutation(
    PersonaToolingService.injectPersona(),
    {
      onCompleted: (d) => onDone(d?.injectPersona),
      onError,
    }
  );

  const [clearInjectedPersonas, { loading: clearing }] = useMutation(
    PersonaToolingService.clearInjectedPersonas(),
    {
      onCompleted: (d) => onDone(d?.clearInjectedPersonas),
      onError,
    }
  );

  if (!enabled) return null;

  const busy = activating || injecting || clearing;
  const personas = data?.listPersonas ?? [];

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={styles.fab}
        title="Persona tooling (staging/local only)"
      >
        Personas
      </button>
    );
  }

  return (
    <div style={styles.panel} role="dialog" aria-label="Persona tooling">
      <div style={styles.header}>
        <strong>Persona tooling</strong>
        <span style={styles.badge}>local/staging</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={styles.close}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <section style={styles.section}>
        <label style={styles.label} htmlFor="persona-picker">
          Select a persona
        </label>
        <div style={styles.row}>
          <select
            id="persona-picker"
            style={styles.select}
            value={selectedId}
            disabled={loading || busy}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">{loading ? "Loading…" : "— choose —"}</option>
            {personas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id} ({p.orderCount} orders
                {p.testTypes.length ? ` · ${p.testTypes.join(", ")}` : ""})
              </option>
            ))}
          </select>
          <button
            type="button"
            style={styles.primary}
            disabled={!selectedId || busy}
            onClick={() => {
              setStatus(null);
              activatePersona({ variables: { personaId: selectedId } });
            }}
          >
            Activate
          </button>
        </div>
      </section>

      <section style={styles.section}>
        <label style={styles.label} htmlFor="persona-payload">
          Paste raw SOS response
        </label>
        <textarea
          id="persona-payload"
          style={styles.textarea}
          value={payload}
          disabled={busy}
          placeholder='{ "data": { "orders": { "content": [ ... ] } } }'
          onChange={(e) => setPayload(e.target.value)}
        />
        <div style={styles.row}>
          <button
            type="button"
            style={styles.primary}
            disabled={!payload.trim() || busy}
            onClick={() => {
              setStatus(null);
              injectPersona({ variables: { payload } });
            }}
          >
            Inject
          </button>
          <button
            type="button"
            style={styles.secondary}
            disabled={busy}
            onClick={() => {
              setStatus(null);
              clearInjectedPersonas();
            }}
          >
            Clear
          </button>
        </div>
      </section>

      {status && (
        <div
          style={{
            ...styles.status,
            ...(status.kind === "error" ? styles.statusError : styles.statusOk),
          }}
        >
          {status.text}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  fab: {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 9999,
    padding: "8px 12px",
    borderRadius: 20,
    border: "none",
    background: "#5b2a86",
    color: "#fff",
    fontSize: 12,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  panel: {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 9999,
    width: 360,
    maxWidth: "90vw",
    background: "#fff",
    border: "1px solid #d0d0d0",
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
    fontSize: 13,
    color: "#222",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderBottom: "1px solid #eee",
    background: "#f6f2fa",
    borderRadius: "8px 8px 0 0",
  },
  badge: {
    fontSize: 10,
    textTransform: "uppercase",
    background: "#5b2a86",
    color: "#fff",
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: 0.5,
  },
  close: {
    marginLeft: "auto",
    border: "none",
    background: "transparent",
    fontSize: 18,
    lineHeight: 1,
    cursor: "pointer",
    color: "#666",
  },
  section: { padding: "12px" },
  label: { display: "block", marginBottom: 6, fontWeight: 600 },
  row: { display: "flex", gap: 8, marginTop: 8, alignItems: "center" },
  select: { flex: 1, padding: "6px 8px", minWidth: 0 },
  textarea: {
    width: "100%",
    minHeight: 120,
    fontFamily: "monospace",
    fontSize: 12,
    padding: 8,
    boxSizing: "border-box",
    resize: "vertical",
  },
  primary: {
    padding: "6px 12px",
    border: "none",
    borderRadius: 4,
    background: "#5b2a86",
    color: "#fff",
    cursor: "pointer",
  },
  secondary: {
    padding: "6px 12px",
    border: "1px solid #bbb",
    borderRadius: 4,
    background: "#fff",
    color: "#333",
    cursor: "pointer",
  },
  status: {
    margin: "0 12px 12px",
    padding: "8px 10px",
    borderRadius: 4,
    fontSize: 12,
    whiteSpace: "pre-wrap",
  },
  statusOk: { background: "#e7f6ec", color: "#1e6b38" },
  statusError: { background: "#fdecea", color: "#a12b1e" },
};

export default PersonaToolingPanel;
